import _ from 'underscore';
import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  FilterType,
  PerformanceEditingFilterBar,
  PerformanceReadonlyFilterBar,
  PerformanceSearchTextFilterComponent,
  getPerformanceFilterComponents,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { NewFilterContextMenu } from './new-filter-context-menu';
/* istanbul ignore next */
export const FilterBarContainerImpl = ({
  filters, addNewFilter, removeAllFilters, removeEmptyFilters, updateFilter, removeFilter,
  allSupportedFilters, isEditingFilter, setIsEditingFilter, onFilterValueChange,
  i18n, ianaTimeZone, dateFormat,
  isAdsStudioAIGCMVPEnabled,
}) => {
  const focusedFilterRef = useRef(null);
  const defaultOpenFilterRef = useRef(null);

  useEffect(() => {
    if (!isEditingFilter) {
      focusedFilterRef.current = null;
    }
  }, [isEditingFilter]);

  const onClickAddFilterButton = useCallback(() => {
    // switch to editing mode, and show the new filter context menu
    focusedFilterRef.current = null;
    setIsEditingFilter(true);
  }, [setIsEditingFilter]);

  const onClickReadonlyFilter = useCallback((filter) => {
    if (filter.type !== FilterType.SearchText) {
      defaultOpenFilterRef.current = filter;
      focusedFilterRef.current = filter;
      setIsEditingFilter(true);
    }
  }, [setIsEditingFilter]);

  const onCloseEditingFilterBar = useCallback(() => {
    setIsEditingFilter(false);
    // ensure no empty filters are left
    removeEmptyFilters();
  }, [removeEmptyFilters, setIsEditingFilter]);

  const onResetClick = useCallback(() => {
    removeAllFilters();
  }, [removeAllFilters]);

  const onAddNewFilter = useCallback((type) => {
    const newFilter = type === FilterType.TopRatedPerformance
      ? addNewFilter({ type, initValue: { topRated: true }, initHumanizedValue: true })
      : addNewFilter({ type });
    focusedFilterRef.current = newFilter;
  }, [addNewFilter]);

  const onRenderNewFilterContextMenu = useCallback(() => (
    <NewFilterContextMenu
      allSupportedFilters={allSupportedFilters}
      filters={filters}
      onAddNewFilter={onAddNewFilter}
      i18n={i18n}
      isFocused={isEditingFilter && !focusedFilterRef.current}
    />
  ), [allSupportedFilters, filters, i18n, isEditingFilter, onAddNewFilter]);

  const onValueChange = useCallback((curFilterState, newValue, newHumanizedValue) => {
    updateFilter({ ...curFilterState, newValue, newHumanizedValue });
    if (onFilterValueChange) {
      onFilterValueChange(curFilterState, newValue, newHumanizedValue);
    }
  }, [updateFilter, onFilterValueChange]);

  const onDropdownDismiss = useCallback((curFilterState, newValue, newHumanizedValue) => {
    // if now value set when dismiss the dropdown, remove the filter
    if (!newHumanizedValue && !curFilterState.isFilterAlwaysVisible) {
      removeFilter(curFilterState);
    }
  }, [removeFilter]);

  const onRenderReadonlyFilter = useCallback(({ filter, isLast }) => {
    const { type, title, humanizedValue } = filter;
    if (type === FilterType.SearchText) {
      return <PerformanceSearchTextFilterComponent i18n={i18n} filter={filter} onValueChange={onValueChange} />;
    }
    if (type === FilterType.TopRatedPerformance) {
      return <React.Fragment>&nbsp;&nbsp;{i18n.getString(title)}{!isLast && ';'}</React.Fragment>;
    }
    return <React.Fragment>&nbsp;&nbsp;{i18n.getString(title)}: {humanizedValue}{!isLast && ';'}</React.Fragment>;
  }, [i18n, onValueChange]);

  const FilterComponents = useMemo(() => getPerformanceFilterComponents({ isAdsStudioAIGCMVPEnabled }), [isAdsStudioAIGCMVPEnabled]);
  const onRenderEditingFilter = useCallback(({ filter, focused }) => {
    const isDefaultOpen = defaultOpenFilterRef.current === filter;
    if (isDefaultOpen) {
      defaultOpenFilterRef.current = null;
    }
    const FilterComponent = FilterComponents[filter.type];
    return FilterComponent ? <FilterComponent
      filter={filter}
      focused={focused}
      defaultOpen={isDefaultOpen}
      onValueChange={onValueChange}
      onDropdownDismiss={onDropdownDismiss}
      i18n={i18n}
      ianaTimeZone={ianaTimeZone}
      dateFormat={dateFormat}
    /> : null;
  }, [FilterComponents, dateFormat, i18n, ianaTimeZone, onDropdownDismiss, onValueChange]);

  return isEditingFilter ? (
    <PerformanceEditingFilterBar
      filters={filters}
      focusedFilter={focusedFilterRef.current}
      onRenderFilter={onRenderEditingFilter}
      onRenderNewFilterContextMenu={onRenderNewFilterContextMenu}
      onCloseClick={onCloseEditingFilterBar}
      onResetClick={onResetClick}
      i18n={i18n}
    />
  ) : (
    <PerformanceReadonlyFilterBar
      filters={filters}
      onRenderFilter={onRenderReadonlyFilter}
      onClickFilter={onClickReadonlyFilter}
      onClickAddFilterButton={onClickAddFilterButton}
      i18n={i18n}
    />
  );
};
/* istanbul ignore next */
FilterBarContainerImpl.propTypes = {
  allSupportedFilters: PropTypes.shape({}).isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({
    // type: PropTypes.string,
    // title: PropTypes.string,
    // id: PropTypes.string,
    // value: PropTypes.any,
    // humanizedValue: PropTypes.string,
  })).isRequired,
  addNewFilter: PropTypes.func,
  removeAllFilters: PropTypes.func,
  removeEmptyFilters: PropTypes.func,
  updateFilter: PropTypes.func,
  removeFilter: PropTypes.func,
  isEditingFilter: PropTypes.bool.isRequired,
  setIsEditingFilter: PropTypes.func.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  dateFormat: PropTypes.string.isRequired,
  isAdsStudioAIGCMVPEnabled: PropTypes.bool,
};
/* istanbul ignore next */
FilterBarContainerImpl.defaultProps = {
  addNewFilter: _.noop,
  removeAllFilters: _.noop,
  removeEmptyFilters: _.noop,
  updateFilter: _.noop,
  removeFilter: _.noop,
  isAdsStudioAIGCMVPEnabled: false,
};

export const FilterBarContainer = React.memo(FilterBarContainerImpl);
